import { EmptyStatePanel, ErrorState, LoadingState } from '@playforge/ui';
import {
  Badge,
  Banner,
  BlockStack,
  Button,
  ButtonGroup,
  Card,
  InlineStack,
  Page,
  Text,
  TextField,
  Toast,
} from '@shopify/polaris';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ScrollArea } from '../components/ScrollArea';
import { apiClient, ApiError } from '../lib/api-client';
import { useApi } from '../lib/use-api';
import { type CampaignStatus, type CampaignView, type CurrentBillingView } from '../lib/types';

type BadgeTone = 'info' | 'success' | 'attention' | 'critical';
const STATUS_TONE: Partial<Record<CampaignStatus, BadgeTone>> = {
  scheduled: 'info',
  published: 'success',
  paused: 'attention',
  expired: 'critical',
};

function CampaignRow({
  campaign,
  onAction,
}: {
  campaign: CampaignView;
  onAction: (action: string, id: string) => void;
}): JSX.Element {
  const navigate = useNavigate();
  return (
    <Card>
      <InlineStack align="space-between" blockAlign="center" gap="300" wrap>
        <BlockStack gap="100">
          <Text as="h3" variant="headingSm">
            {campaign.campaignName}
          </Text>
          <Badge {...(STATUS_TONE[campaign.status] ? { tone: STATUS_TONE[campaign.status] } : {})}>
            {campaign.status}
          </Badge>
        </BlockStack>
        <ButtonGroup>
          <Button onClick={() => navigate(`/campaigns/${campaign.id}`)}>Edit</Button>
          {campaign.status === 'published' ? (
            <Button onClick={() => onAction('pause', campaign.id)}>Pause</Button>
          ) : campaign.status === 'archived' || campaign.status === 'expired' ? (
            // Archived/expired can't publish directly — restore to draft first.
            <Button variant="primary" onClick={() => onAction('restore', campaign.id)}>
              Restore
            </Button>
          ) : (
            <Button variant="primary" onClick={() => onAction('publish', campaign.id)}>
              Publish
            </Button>
          )}
          <Button onClick={() => onAction('duplicate', campaign.id)}>Duplicate</Button>
          {campaign.status !== 'archived' ? (
            <Button tone="critical" onClick={() => onAction('archive', campaign.id)}>
              Archive
            </Button>
          ) : null}
        </ButtonGroup>
      </InlineStack>
    </Card>
  );
}

export function CampaignsPage(): JSX.Element {
  const navigate = useNavigate();
  const { data, loading, error, refetch } = useApi(
    () => apiClient.get<CampaignView[]>('/campaigns?limit=100'),
    [],
  );
  const billing = useApi(() => apiClient.get<CurrentBillingView>('/billing/current'), []);
  const [newName, setNewName] = useState('');
  const [busy, setBusy] = useState(false);
  const [toast, setToast] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);

  // Plan caps (-1 = unlimited). Fail open if billing can't be read — the server
  // still enforces the limit, so we only use this to nudge before the error.
  const maxCampaigns = billing.data?.limits?.maxCampaigns ?? -1;
  const planName = billing.data?.plan?.name ?? 'your plan';
  // Archived campaigns don't count toward the cap (matches the server) — so
  // archiving one frees a slot, as the banner tells the merchant.
  const campaignCount = (data ?? []).filter((c) => c.status !== 'archived').length;
  const atCap = maxCampaigns >= 0 && campaignCount >= maxCampaigns;

  const create = async (): Promise<void> => {
    if (!newName.trim() || atCap) return;
    setBusy(true);
    setActionError(null);
    try {
      const campaign = await apiClient.post<CampaignView>('/campaigns', { campaignName: newName });
      navigate(`/campaigns/${campaign.id}`);
    } catch (err) {
      setActionError(err instanceof ApiError ? err.message : 'Could not create campaign');
    } finally {
      setBusy(false);
    }
  };

  const runAction = async (action: string, id: string): Promise<void> => {
    setActionError(null);
    try {
      await apiClient.post(`/campaigns/${id}/${action}`);
      setToast(`Campaign ${action}d`);
      refetch();
    } catch (err) {
      setActionError(err instanceof ApiError ? err.message : `Could not ${action} campaign`);
    }
  };

  return (
    <Page title="Campaigns" subtitle="Create and manage your gamified campaigns">
      <BlockStack gap="400">
        {atCap ? (
          <Banner
            tone="warning"
            title={`You've reached the campaign limit for ${planName}`}
            action={{ content: 'Upgrade plan', onAction: () => navigate('/billing') }}
          >
            <p>
              {planName} includes {maxCampaigns} campaign{maxCampaigns === 1 ? '' : 's'}. Upgrade to
              create more, or archive an existing campaign to free up a slot.
            </p>
          </Banner>
        ) : null}

        <Card>
          <InlineStack gap="200" blockAlign="end">
            <div style={{ flexGrow: 1 }}>
              <TextField
                label="New campaign name"
                value={newName}
                onChange={setNewName}
                autoComplete="off"
                placeholder="Spring Spin to Win"
                disabled={atCap}
              />
            </div>
            <Button variant="primary" loading={busy} disabled={atCap} onClick={() => void create()}>
              Create
            </Button>
          </InlineStack>
        </Card>

        {actionError ? <ErrorState message={actionError} /> : null}
        {loading ? <LoadingState label="Loading campaigns" /> : null}
        {error ? <ErrorState message={error.message} onRetry={refetch} /> : null}

        {data && data.length === 0 ? (
          <Card>
            <EmptyStatePanel heading="No campaigns yet">
              <p>Name your first campaign above to get started.</p>
            </EmptyStatePanel>
          </Card>
        ) : null}

        {data && data.length > 0 ? (
          <ScrollArea maxHeight={620}>
            <BlockStack gap="300">
              {data.map((campaign) => (
                <CampaignRow
                  key={campaign.id}
                  campaign={campaign}
                  onAction={(a, id) => void runAction(a, id)}
                />
              ))}
            </BlockStack>
          </ScrollArea>
        ) : null}
      </BlockStack>
      {toast ? <Toast content={toast} onDismiss={() => setToast(null)} /> : null}
    </Page>
  );
}
